import { useState, useEffect } from 'react';
import { testimonialsData } from '../../utils/testimonialsData';

interface TestimonialsCarouselProps {
  autoPlayInterval?: number;
  className?: string;
}

export default function TestimonialsCarousel({ 
  autoPlayInterval = 5000,
  className = ""
}: TestimonialsCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-play functionality
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex === testimonialsData.length - 1 ? 0 : prevIndex + 1;
        return nextIndex;
      });
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlayInterval, isPaused]);

  const handleNext = () => {
    if (isAnimating) return;
    setIsPaused(true);
    setIsAnimating(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonialsData.length - 1 ? 0 : prevIndex + 1
    );
    setTimeout(() => {
      setIsAnimating(false);
      setTimeout(() => setIsPaused(false), 1000); // Resume auto-play after 1 second
    }, 200);
  };

  const handlePrevious = () => {
    if (isAnimating) return;
    setIsPaused(true);
    setIsAnimating(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonialsData.length - 1 : prevIndex - 1
    );
    setTimeout(() => {
      setIsAnimating(false);
      setTimeout(() => setIsPaused(false), 1000); // Resume auto-play after 1 second
    }, 200);
  };

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentIndex) return;
    setIsPaused(true);
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => {
      setIsAnimating(false);
      setTimeout(() => setIsPaused(false), 1000); // Resume auto-play after 1 second
    }, 200);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <i
        key={index}
        className={`ri-star-${index < rating ? 'fill' : 'line'} text-yellow-400 text-sm`}
      />
    ));
  };

  const currentTestimonial = testimonialsData[currentIndex];

  return (
    <div className={`w-full max-w-2xl mx-auto ${className}`}>
      {/* Main testimonial card */}
      <div className="relative bg-white/95 backdrop-blur-sm rounded-card p-3 px-12 shadow-card border border-white/20 min-h-[120px]">
        <div className={`transition-all duration-200 ${isAnimating ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'}`}>
          {/* 1. Ícone circular */}
          <div className="flex justify-center mb-2">
            {currentTestimonial.avatar ? (
              <div className="w-12 h-12 rounded-full overflow-hidden shadow-sm">
                <img 
                  src={currentTestimonial.avatar} 
                  alt={`Avatar de ${currentTestimonial.name}`} 
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-base font-sans">
                  {currentTestimonial.name.charAt(0)}
                </span>
              </div>
            )}
          </div>

          {/* 2. Nome do usuário */}
          <div className="text-center mb-2">
            <h4 className="font-semibold text-gray-800 text-base font-sans" style={{ fontWeight: 700 }}>
              {currentTestimonial.name}
            </h4>
          </div>

          {/* 3. Depoimento */}
          <blockquote className="text-gray-700 text-center mb-2 leading-relaxed font-sans text-sm" style={{ fontWeight: 500 }}>
            "{currentTestimonial.content}"
          </blockquote>

          {/* 4. Estrelas */}
          <div className="flex justify-center">
            {renderStars(currentTestimonial.rating)}
          </div>
        </div>

        {/* Navigation arrows */}
        <button
          onClick={handlePrevious}
          disabled={isAnimating}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm border border-white/30 hover:bg-white/90 transition-all duration-200 disabled:opacity-50 z-10"
        >
          <i className="ri-arrow-left-s-line text-gray-600 text-sm"></i>
        </button>
        
        <button
          onClick={handleNext}
          disabled={isAnimating}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm border border-white/30 hover:bg-white/90 transition-all duration-200 disabled:opacity-50 z-10"
        >
          <i className="ri-arrow-right-s-line text-gray-600 text-sm"></i>
        </button>
      </div>

      {/* Dots indicator */}
      <div className="flex justify-center mt-3 space-x-2">
        {testimonialsData.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            disabled={isAnimating}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              index === currentIndex 
                ? 'bg-white shadow-sm' 
                : 'bg-white/50 hover:bg-white/70'
            } disabled:opacity-50`}
          />
        ))}
      </div>

      {/* Progress indicator */}
      <div className="mt-2 w-full bg-white/20 rounded-full h-1 overflow-hidden">
        <div 
          className="h-full bg-white/60 rounded-full transition-all duration-200"
          style={{ 
            width: `${((currentIndex + 1) / testimonialsData.length) * 100}%` 
          }}
        />
      </div>
    </div>
  );
}